import { DateTime } from 'luxon'
import React, { useMemo, useState } from 'react'
import Text from '../../../../components/atoms/text/Text'
import { useMeetingRoomOccupancy, useOccupancy } from '../../../../modules/seats/hooks'
import styles from './Occupancy.module.css'
import { useNavigate } from 'react-router-dom'
import routes from '../../../../config/routes'
import DaySelector from '../../../../components/molecules/daySelector/DaySelector'
import { MeetingRoomOccupancy } from '../../../../types/app.types'
import Button from '../../../../components/atoms/button/Button'
import { VARIANTS } from '../../../../types/Components.types'
import c from 'classnames'

interface OccupancyByDay {
  day: string
  occupied: number
  available: number
  percentage: number
  numOfSeats: number
}

const Occupancy = () => {
  const navigate = useNavigate()
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const { data: occupancy } = useOccupancy({})
  const { data: meetingRoomOccupancy, isLoading: isLoadingMeetingRoom } = useMeetingRoomOccupancy({
    date: selectedDate,
  })
  const [selectedWeek, setSelectedWeek] = useState<Date>(DateTime.local().startOf('week').toJSDate())

  const goToApp = () => {
    navigate(routes.app)
  }

  const handleDateChange = (date: Date) => {
    setSelectedDate(date)
  }

  const isWeekend = useMemo(() => {
    const selectedDay = DateTime.fromJSDate(selectedDate)
    return selectedDay.weekday >= 6 // Saturday (6) or Sunday (7)
  }, [selectedDate])

  const [seatTotals, blueTotals] = useMemo(() => {
    if (!occupancy) return [0, 0]
    return Object.keys(occupancy.total).reduce(
      (total, seatType) => {
        if (seatType === 'blue') {
          return [total[0], total[1] + occupancy.total.blue]
        }
        return [total[0] + occupancy.total[seatType as keyof typeof occupancy.total], total[1]]
      },
      [0, 0],
    )
  }, [occupancy])

  const { regularData, blueData }: { regularData: OccupancyByDay[]; blueData: OccupancyByDay[] } = useMemo(() => {
    const days = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes']
    if (!occupancy) {
      return {
        regularData: days.map((day) => ({ day, occupied: 0, available: 0, percentage: 0, numOfSeats: 0 })),
        blueData: days.map((day) => ({ day, occupied: 0, available: 0, percentage: 0, numOfSeats: 0 })),
      }
    }

    const regularData: OccupancyByDay[] = []
    const blueData: OccupancyByDay[] = []
    let dayIndex = 0

    Object.entries(occupancy.occupancy).forEach(([date, data]) => {
      const day = DateTime.fromISO(date)
      if (day.weekday >= 6) {
        // Skip weekends
        return
      }

      if (day >= DateTime.fromJSDate(selectedWeek) && day < DateTime.fromJSDate(selectedWeek).plus({ weeks: 1 })) {
        const occupiedSeats = data.red + data.yellow
        const availableSeats = seatTotals - occupiedSeats
        const percentage = seatTotals > 0 ? Math.round((occupiedSeats / seatTotals) * 100) : 0

        regularData.push({
          day: `${days[dayIndex]} ${day.day}` || `Day ${dayIndex + 1}`,
          occupied: occupiedSeats,
          available: availableSeats,
          percentage,
          numOfSeats: seatTotals,
        })
        blueData.push({
          day: `${days[dayIndex]} ${day.day}` || `Day ${dayIndex + 1}`,
          occupied: data.blue,
          available: blueTotals - data.blue,
          percentage: blueTotals > 0 ? Math.round((data.blue / blueTotals) * 100) : 0,
          numOfSeats: blueTotals,
        })
        dayIndex++
      }
    })

    return { regularData, blueData }
  }, [occupancy, selectedWeek, seatTotals, blueTotals])

  const getAvailabilityColor = (percentage: number) => {
    if (percentage < 40) return 'green' // Low occupancy = high availability
    if (percentage < 80) return 'yellow' // Medium occupancy
    return 'red' // High occupancy = low availability
  }

  const currentWeek = DateTime.local().startOf('week').toJSDate()
  const nextWeek = DateTime.local().startOf('week').plus({ weeks: 1 }).toJSDate()

  const getWeekLabel = (weekDate: Date) => {
    const week = DateTime.fromJSDate(weekDate)
    const startDay = week.toFormat('d MMM')
    const endDay = week.plus({ days: 6 }).toFormat('d MMM')
    return `${startDay} - ${endDay}`
  }

  const handleWeekChange = (week: Date) => {
    setSelectedWeek(week)
  }

  const meetingRoomStatus: { morning: MeetingRoomOccupancy[]; afternoon: MeetingRoomOccupancy[] } = useMemo(() => {
    if (!meetingRoomOccupancy) {
      return {
        morning: [],
        afternoon: [],
      }
    }

    const result: { morning: MeetingRoomOccupancy[]; afternoon: MeetingRoomOccupancy[] } = {
      morning: [],
      afternoon: [],
    }

    if (meetingRoomOccupancy.length) {
      for (const slot of meetingRoomOccupancy) {
        const slotDate = DateTime.fromISO(slot.hour)
        const formattedHour = DateTime.fromISO(slot.hour).toFormat('HH:mm')
        const slotData = {
          hour: formattedHour,
          status: slot.status,
        }

        if (slotDate.hour <= 14) {
          result.morning.push(slotData)
        } else {
          result.afternoon.push(slotData)
        }
      }
    }

    return result
  }, [meetingRoomOccupancy])

  const renderOccupancy = (data: OccupancyByDay[]) => {
    return data.map((dayData) => (
      <div
        key={dayData.day}
        className={`${styles.dayCard} ${styles[`availability-${getAvailabilityColor(dayData.percentage)}`]}`}
      >
        <Text size="m" weight="600" className={styles.dayName}>
          {dayData.day}
        </Text>

        <div className={styles.availability}>
          <Text size="xl" weight="700" className={styles.availableCount}>
            {dayData.available}
          </Text>
          <Text size="s" className={styles.availableLabel}>
            disponibles
          </Text>
        </div>

        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${dayData.percentage}%` }} />
        </div>

        <Text size="s" className={styles.totalSeats}>
          de {dayData.numOfSeats} total
        </Text>
      </div>
    ))
  }

  return (
    <section className={styles.container}>
      <Text weight="500" size="xl" transform="uppercase" align="center" className={styles.title}>
        Ocupación
      </Text>

      <Text size="ml" align="center">
        La ocupación del coworking se mide en función de las reservas realizadas. En este gráfico puedes ver la
        ocupación de la semana actual.
      </Text>

      <Text size="ml" align="center">
        Recuerda que el horario del coworking es de{' '}
        <Text element="span" weight="600">
          lunes a viernes de 8:00 a 21:00 horas
        </Text>
        . Los sábados y domingos el coworking permanecerá cerrado.
      </Text>

      <div className={styles.weekSelector}>
        <button
          className={`${styles.weekTag} ${
            selectedWeek.getTime() === currentWeek.getTime() ? styles.weekTagActive : ''
          }`}
          onClick={() => handleWeekChange(currentWeek)}
        >
          <Text size="s" weight="600">
            Esta semana
          </Text>
          <Text size="xs" className={styles.weekTagDate}>
            {getWeekLabel(currentWeek)}
          </Text>
        </button>

        <button
          className={`${styles.weekTag} ${selectedWeek.getTime() === nextWeek.getTime() ? styles.weekTagActive : ''}`}
          onClick={() => handleWeekChange(nextWeek)}
        >
          <Text size="s" weight="600">
            Próxima semana
          </Text>
          <Text size="xs" className={styles.weekTagDate}>
            {getWeekLabel(nextWeek)}
          </Text>
        </button>
      </div>

      <article className={styles.occupancyGrid}>{renderOccupancy(regularData)}</article>

      <Text weight="500" size="xl" transform="uppercase" align="center" className={styles.blueSeatsTitle}>
        Sitios Azules
      </Text>

      <article className={c(styles.occupancyGrid, styles.blueGrid)}>{renderOccupancy(blueData)}</article>

      <div className={styles.captionWrapper}>
        <Text className={styles.caption} size="m" align="center">
          <span className={styles.indicator} data-green="true" /> Alta disponibilidad
        </Text>

        <span className={styles.separator}> / </span>

        <Text className={styles.caption} size="m" align="center">
          <span className={styles.indicator} data-yellow="true" /> Media disponibilidad
        </Text>

        <span className={styles.separator}> / </span>

        <Text className={styles.caption} size="m" align="center">
          <span className={styles.indicator} data-red="true" /> Baja disponibilidad
        </Text>
      </div>

      <article className={styles.meetingRoom}>
        <Text weight="500" size="xl" transform="uppercase" align="center" className={styles.title}>
          Sala de reuniones
        </Text>

        <Text size="ml" align="center">
          El coworking cuenta con una sala de reuniones que puedes reservar por horas. Debido a la alta demanda te
          recomendamos que reserves con antelación. Para poder acceder a la sala de reuniones debes ser usuario del
          coworking y tener una reserva activa de un puesto de trabajo el día que quieras reservar la sala.
        </Text>

        <DaySelector
          className={styles.daySelector}
          selectedDate={selectedDate}
          onDateChange={handleDateChange}
          calendarEnabled={false}
        />

        {isWeekend && (
          <Text size="ml" align="center" className={styles.weekendWarning}>
            ⚠️ El coworking permanece cerrado los fines de semana. La sala de reuniones no está disponible sábados y
            domingos.
          </Text>
        )}

        <div className={styles.slots}>
          <div>
            <Text weight="600" size="ml" transform="uppercase" align="center" className={styles.title}>
              Mañanas
            </Text>

            <div className={styles.slotsContainer}>
              {isLoadingMeetingRoom ? (
                <Text size="ml" align="center">
                  Cargando...
                </Text>
              ) : meetingRoomStatus.morning.length > 0 ? (
                meetingRoomStatus.morning.map((slot, index) => (
                  <div key={index} className={c(styles.slot, { [styles.slotDisabled]: isWeekend })}>
                    <span className={styles.indicator} data-reserved={slot.status === 'busy'} />
                    <Text size="ml">{slot.hour}</Text>
                  </div>
                ))
              ) : (
                <Text size="ml" align="center">
                  No hay datos disponibles
                </Text>
              )}
            </div>
          </div>

          <div>
            <Text weight="600" size="ml" transform="uppercase" align="center" className={styles.title}>
              Tardes
            </Text>

            <div className={styles.slotsContainer}>
              {isLoadingMeetingRoom ? (
                <Text size="ml" align="center">
                  Cargando...
                </Text>
              ) : meetingRoomStatus.afternoon.length > 0 ? (
                meetingRoomStatus.afternoon.map((slot, index) => (
                  <div key={index} className={c(styles.slot, { [styles.slotDisabled]: isWeekend })}>
                    <span className={styles.indicator} data-reserved={slot.status === 'busy'} />
                    <Text size="ml">{slot.hour}</Text>
                  </div>
                ))
              ) : (
                <Text size="ml" align="center">
                  No hay datos disponibles
                </Text>
              )}
            </div>
          </div>
        </div>

        <Button variant={VARIANTS.primary} onClick={goToApp} className={styles.cta}>
          ¡Reserva ahora!
        </Button>
      </article>
    </section>
  )
}

export default React.memo(Occupancy)
